const SsoUtils = require('utils/sso');
const Storage = require('utils/storage');
const Debug = require('./debug');
const Policy = require('./policy');
const PersistenceConfig = require('./persistence-config');
const AuthConstants = require('./constants');
const SchoolPolicy = require('services/school-policy');

const LAST_SUCCESSFUL_SSO_EMAIL_STORAGE_KEY = PersistenceConfig.keys.sso.lastSuccessfulEmail;

const ROUTE_CONTEXT_FIELDS = [
    'email',
    'sso',
    'schoolId',
    'roleId',
    'verificationToken',
    'mfaMethod',
    'authMethod',
    'redirect',
    'redirectTarget',
    'policy',
    'forceSSOLoginPromptEmail',
    'skipSSOLoginPromptEmail'
];

module.exports = {
    normalizeSSO,
    getSSOProvider,
    getSSOCallbackUri,
    getEmailFromClaims,
    doesAuthenticatedEmailMatch,
    getCallbackContext,
    setCallbackContext,
    clearCallbackContext,
    buildRouteContextPayload,
    getCallbackRouteContextUpdate,
    getEffectiveRouteContext,
    hasRequiredRouteContext,
    buildSSORedirectStartedContextUpdate,
    getLastSuccessfulSSOEmail,
    rememberSuccessfulSSOEmail,
    shouldForceSSOLoginPrompt,
    shouldForceSsoReauthentication,
    getSsoInspection
};

function normalizeSSO(sso) {

    return SsoUtils.normalizeSSO(sso);
}

function getSSOProvider(sso) {

    return SsoUtils.getSSOProvider(normalizeSSO(sso));
}

function getSSOCallbackUri({ provider, flow }) {

    return SsoUtils.getSSOCallbackUri({
        provider,
        flow
    });
}

function getEmailFromClaims(claims) {

    return SsoUtils.getEmailFromClaims(claims);
}

function doesAuthenticatedEmailMatch({ authenticatedEmail, expectedEmail } = {}) {

    const normalizedAuthenticatedEmail = Policy.normalizeEmail(authenticatedEmail);
    const normalizedExpectedEmail = Policy.normalizeEmail(expectedEmail);

    return !!(
        normalizedAuthenticatedEmail
        && normalizedExpectedEmail
        && normalizedAuthenticatedEmail === normalizedExpectedEmail
    );
}

function getCallbackContext() {

    return SsoUtils.getCallbackContext();
}

function setCallbackContext(callbackContext) {

    return SsoUtils.setCallbackContext(callbackContext);
}

function clearCallbackContext() {

    return SsoUtils.clearCallbackContext();
}

function buildRouteContextPayload(routeContext = {}) {

    return ROUTE_CONTEXT_FIELDS.reduce((collector, key) => {

        if (typeof routeContext[key] === 'undefined') {
            return collector;
        }

        return {
            ...collector,
            [key]: key === 'sso' ? normalizeSSO(routeContext[key]) : routeContext[key]
        };
    }, {});
}

function getEffectiveRouteContext({
    callbackContext,
    flow,
    isCallbackRequest,
    persistedRouteContext,
    routeContext
}) {

    const persistedFields = buildRouteContextPayload(persistedRouteContext);
    const routeFields = buildRouteContextPayload(routeContext);
    const effectiveRouteContext = {
        ...persistedFields,
        ...routeFields
    };

    if (!isCallbackRequest || callbackContext?.flow !== flow) {
        return effectiveRouteContext;
    }

    const callbackFields = buildRouteContextPayload(callbackContext);

    return {
        ...effectiveRouteContext,
        ...callbackFields,
        flow: callbackContext.flow
    };
}

function getCallbackRouteContextUpdate({
    callbackContext,
    flow,
    isCallbackRequest,
    routeContext
}) {

    if (!isCallbackRequest || callbackContext?.flow !== flow) {
        return null;
    }

    if (routeContext.email && routeContext.sso) {
        return null;
    }

    return buildRouteContextPayload(callbackContext);
}

function hasRequiredRouteContext({ email, redirectUri, sso }) {

    return !!(email && normalizeSSO(sso) && redirectUri);
}

function getLastSuccessfulSSOEmail() {

    return Policy.normalizeEmail(Storage.get(LAST_SUCCESSFUL_SSO_EMAIL_STORAGE_KEY));
}

function rememberSuccessfulSSOEmail({ email } = {}) {

    const normalizedEmail = Policy.normalizeEmail(email);

    if (!normalizedEmail) {
        return null;
    }

    Storage.set(LAST_SUCCESSFUL_SSO_EMAIL_STORAGE_KEY, normalizedEmail);

    Debug.recordFlow('sso_successful_email_remembered', {
        email: normalizedEmail
    });

    return normalizedEmail;
}

function shouldForceSSOLoginPrompt({
    email,
    platform = SchoolPolicy.getClientPlatform(),
    policy,
    provider
} = {}) {

    if (provider !== 'keycloak') {
        return false;
    }

    const normalizedEmail = Policy.normalizeEmail(email);
    const policyRequiresPrompt = shouldForceSsoReauthentication({
        policy,
        platform,
        provider
    });

    return !!normalizedEmail || policyRequiresPrompt;
}

function shouldForceSsoReauthentication({
    platform = SchoolPolicy.getClientPlatform(),
    policy,
    provider
} = {}) {

    if (provider !== 'keycloak') {
        return false;
    }

    return SchoolPolicy.isSchoolPolicyRuleEnabled({
        policy,
        rule: 'forceSsoReauthentication',
        platform
    });
}

function buildSSORedirectStartedContextUpdate({ routeContext = {} } = {}) {

    if (!routeContext.email || routeContext.forceSSOLoginPromptEmail !== routeContext.email) {
        return null;
    }

    return {
        forceSSOLoginPromptEmail: null,
        skipSSOLoginPromptEmail: routeContext.email
    };
}

function getSsoInspection({
    loginContext = {},
    signupContext = {},
    currentSchool = {},
    persisted = {}
} = {}) {

    const callbackContext = getCallbackContext();
    const persistedLoginContext = persisted.loginContext || {};
    const persistedSignupContext = persisted.signupContext || {};
    const persistedCallbackContext = persisted.ssoCallbackContext || {};

    const candidates = [
        { source: 'redux.loginContext', value: loginContext.sso },
        { source: 'redux.signupContext', value: signupContext.sso },
        { source: 'redux.currentSchool', value: currentSchool?.sso },
        { source: 'persisted.loginContext', value: persistedLoginContext.sso },
        { source: 'persisted.signupContext', value: persistedSignupContext.sso },
        { source: 'persisted.ssoCallbackContext', value: persistedCallbackContext.sso },
        { source: 'callbackContext', value: callbackContext?.sso }
    ];

    const selected = candidates.find(({ value }) => normalizeSSO(value));
    const rawSso = selected?.value || null;
    const sso = normalizeSSO(rawSso);
    const provider = sso ? getSSOProvider(sso) : null;
    const userEmail = loginContext.email
        || signupContext.email
        || persistedLoginContext.email
        || persistedSignupContext.email
        || callbackContext?.email
        || null;
    const lastSuccessfulSSOEmail = getLastSuccessfulSSOEmail();
    const willRequestPromptLogin = shouldForceSSOLoginPrompt({
        email: userEmail,
        provider
    });
    const authMethod = loginContext.authMethod
        || signupContext.authMethod
        || persistedLoginContext.authMethod
        || persistedSignupContext.authMethod
        || (sso ? AuthConstants.authFlow.methods.sso : AuthConstants.authFlow.methods.password);

    return {
        rawSso,
        sso,
        provider,
        source: selected?.source || null,
        candidates: candidates.map(({ source, value }) => ({
            source,
            rawSso: value || null,
            sso: normalizeSSO(value),
            provider: normalizeSSO(value) ? getSSOProvider(value) : null
        })),
        promptLogin: {
            managedBy: 'auth-management',
            lastSuccessfulSSOEmail,
            userEmail,
            willRequestPromptLogin,
            promptParam: willRequestPromptLogin ? 'prompt=login' : null,
            reason: willRequestPromptLogin ? 'email_changed_or_first_keycloak_login' : null
        },
        loginMode: {
            authMethod,
            decidedBy: authMethod === AuthConstants.authFlow.methods.mfa
                ? 'mfa_configuration_override'
                : (sso ? 'api_school_check_or_login_required_response' : 'no_sso_context_available')
        },
        loginContext: buildRouteContextPayload(loginContext),
        signupContext: buildRouteContextPayload(signupContext),
        persisted: {
            loginContext: buildRouteContextPayload(persistedLoginContext),
            signupContext: buildRouteContextPayload(persistedSignupContext),
            ssoCallbackContext: buildRouteContextPayload(persistedCallbackContext)
        },
        currentSchool: currentSchool ? {
            id: currentSchool.id,
            name: currentSchool.name,
            sso: normalizeSSO(currentSchool.sso),
            provider: normalizeSSO(currentSchool.sso) ? getSSOProvider(currentSchool.sso) : null
        } : null,
        callbackContext
    };

}
